import { Helmet } from 'react-helmet';
import { Divider, Form } from 'antd';
import { useIntl } from 'react-intl';
import { PageProps } from '@/types/PageProps';
import { DefaultRouteTabs } from '@/components/_common';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import ConsolidatedForm from '@/components/Reports/StockReports/ConsolidatedForm';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useQuestReportConsolidated } from '@/core/domain/questReport/Reports/Consolidated/Consolidated.hooks';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import {
  QuestConsolidatedFormValues,
  QuestConsolidatedReportType,
  QuestConsolidatedRequest,
} from '@/core/domain/questReport/Reports/Consolidated/Consolidated.types';
import { QuestResponseType } from '@/core/domain/questReport/Reports/Quotation/Quotation.types';
import { useReportHandlers } from '@/hooks/useReportHandler';

function ConsolidatedPage({ route }: PageProps) {
  const [form] = Form.useForm<QuestConsolidatedFormValues>();
  const intl = useIntl();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const generateReport = useQuestReportConsolidated();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const {
        companiesIds,
        filterByIds,
        reportType,
        initialDate,
        finalDate,
        ...values
      } = await form.validateFields();

      const obj: QuestConsolidatedRequest = {
        ...values,
        companiesIds: companiesIds?.toString(),
        filterByIds: filterByIds?.toString(),
        responseType:
          format === 'CSV' ? QuestResponseType.CSV : QuestResponseType.PDF,
        isSynthetic: reportType.includes(QuestConsolidatedReportType.synthetic),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const { handleView, handlePdf, handleCsv } = useReportHandlers(
    'relatorio_consolidado',
    handleGenerateReport,
  );

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <ConsolidatedForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default ConsolidatedPage;
